import { Search } from "react-feather";
import { Button, CardBody, Input, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";
import PaginationComponent from "Component/Learning/Common/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react";
import { attachDetachFAQProgram, handleBackPagination, handleClickPagination, handleNextPagination } from "Features/Learning/FAQProgram/Utils/FAQProgramUtils";
import { setFilters } from "Features/Learning/FAQProgram/faqProgramSlice";
import { fetchMainProgram } from "Features/Learning/MainPrograms/Utils/MainProgramUtils";
import { handleGetDetailMembership } from "Component/Learning/Membership/MembershipsUtils";
import { setDetail } from "Features/Learning/MainPrograms/mainProgramSlice";
import Swal from "sweetalert2";
import { fetchFAQPrograms } from "Component/Learning/FAQProgram/FAQProgramUtils";

const ModalAddFAQ = ({ items, programId }) => {
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.MainProgram.filters);
  const filter = useSelector((state) => state.FAQProgram.filters);
  const [modal, setModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [initialSnapshot, setInitialSnapshot] = useState([]); // snapshot saat modal dibuka
  const FAQSearchResult = useSelector((state) => state.FAQProgram.data.data);
  const isLoading = useSelector((state) => state.FAQProgram.isLoading);
  const pagination = useSelector((state) => state.FAQProgram.data.pagination);
  const [refatch, setRefatch] = useState(false);
  const lastFetchRef = useRef('');

  useEffect(() => {
    // hanya fetch saat modal terbuka
    if (!modal) return;

    // buat key sederhana dari bagian filter yang penting (search + page)
    const key = JSON.stringify({
      q: filter["filter[question]"] || "",
      page: filter.page || 1,
    });

    // jika key sama dan tidak ada permintaan refetch, skip
    if (lastFetchRef.current === key && !refatch) return;
    lastFetchRef.current = key;

    // debounce supaya typing tidak memicu banyak request
    const timer = setTimeout(() => {
      fetchFAQPrograms(dispatch, filter, setRefatch);
    }, 250);

    return () => clearTimeout(timer);
  }, [modal, filter["filter[question]"], filter.page, refatch, dispatch]);

  useEffect(() => {
    if (items?.length > 0) {
      setSelectedItems(items);
      setSelectedIds(items?.map((item) => item.id));
    }
  }, [items]);

  // close modal tanpa konfirmasi (dipakai setelah submit sukses)
  const closeModal = () => {
    const itemsArr = Array.isArray(items) ? items : [];
    setSelectedItems(itemsArr.length > 0 ? itemsArr : []);
    setSelectedIds(itemsArr.length > 0 ? itemsArr.map((it) => it.id) : []);
    setInitialSnapshot(itemsArr.length > 0 ? itemsArr : []);
    setModal(false);
  };

  // toggle modal dengan konfirmasi bila ada perubahan terhadap snapshot awal
  const toggleModal = async () => {
    if (!modal) {
      // opening: inisialisasi selected dari props dan simpan snapshot
      const itemsArr = Array.isArray(items) ? items : [];
      setSelectedItems(itemsArr.length > 0 ? itemsArr : []);
      setSelectedIds(itemsArr.length > 0 ? itemsArr.map((it) => it.id) : []);
      setInitialSnapshot(itemsArr.length > 0 ? itemsArr : []);
      setModal(true);
      return;
    }

    // closing: bandingkan selectedIds dengan initialSnapshot ids
    const initialIds = (initialSnapshot || []).map((i) => i.id).slice().sort().join(",");
    const currentIds = (selectedIds || []).slice().sort().join(",");

    if (initialIds !== currentIds) {
      const result = await Swal.fire({
        title: "Apakah anda yakin?",
        text: "Perubahan yang belum disimpan akan dibatalkan.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Ya",
        cancelButtonColor: "#d33",
      });
      if (!result.isConfirmed) return;
    }

    // restore ke snapshot awal dan tutup modal
    const original = initialSnapshot || [];
    setSelectedItems(original.length > 0 ? original : []);
    setSelectedIds(original.length > 0 ? original.map((it) => it.id) : []);
    setModal(false);
  };

  const handleCheckboxChange = (item) => {
    if (selectedIds.includes(item.id)) {
      const newSelectedItems = selectedItems.filter((value) => value.id !== item.id);
      const newSelectedIds = selectedIds.filter((id) => id !== item.id);
      setSelectedItems(newSelectedItems);
      setSelectedIds(newSelectedIds);
    } else {
      setSelectedItems([...selectedItems, item]);
      setSelectedIds([...selectedIds, item.id]);
    }
  };

  const handleClickSearch = () => {
    setRefatch(!refatch);
  };

  const handleSubmit = async () => {
    const newFAQs = selectedItems
      .filter(
        (item, index, self) => self.findIndex((t) => t.id === item.id) === index
      )
      .map((item) => {
        return {
          value: item.id,
        };
      });

    const data = {
      faqs: newFAQs,
      id: programId,
    };

    try {
      const response = await dispatch(attachDetachFAQProgram(data));

      if (response.payload === true) {
        // close without confirmation after successful save
        closeModal();
        const getMainProgram = await dispatch(
          fetchMainProgram({
            ...filters,
            "filter[id]": programId,
            page: "1",
          })
        );

        handleGetDetailMembership(dispatch, getMainProgram.payload.data[0], setDetail)
        Swal.fire("Berhasil!", "FAQ Berhasil Ditambahkan", "success");
      }
    } catch (error) {
      console.error("Error:", error);
      // Modal tidak akan ditutup jika terjadi kesalahan
    }
  };

  return (
    <>
      <Button color="primary" onClick={toggleModal}>
        <i className={"fa fa-plus-square fa-lg me-2"}></i>
        Tambah FAQ
      </Button>{" "}
      <Modal size="lg" isOpen={modal} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          Tambahkan FAQ ke Program
        </ModalHeader>
        <ModalBody>
          <ul>
            {selectedItems.length > 0 &&
              selectedItems.map((item, index) => (
                <li key={`item-${index}`}>
                  {item.id} - {item.question}
                </li>
              ))}
          </ul>
          <div className="faq-form mt-3">
            <Input
              className="form-control"
              type="text"
              placeholder="Search.."
              value={filter["filter[question]"]}
              onKeyUp={(e) => (e.key === "Enter" ? handleClickSearch(e) : null)}
              onChange={(e) => {
                dispatch(
                  setFilters({
                    ...filter,
                    "filter[question]": e.target.value,
                  })
                );
              }}
            />
            <Search className="search-icon" />
          </div>
          <Table className="table-responsive table-bordered my-3">
            <thead className="table-light">
              <tr>
                <th scope="col">{"#"}</th>
                <th scope="col">{"ID"}</th>
                <th scope="col">Pertanyaan</th>
                <th scope="col">Jawaban</th>
                <th scope="col">Terlihat</th>
                <th scope="col" className="text-center" colSpan="2">
                  {"Aksi"}
                </th>
              </tr>
            </thead>
            <tbody>
              {FAQSearchResult &&
                FAQSearchResult.map((item, index) => (
                  <tr key={`item-${index}`}>
                    <th>{index + 1}</th>
                    <th>{item.id}</th>
                    <td>{item.question}</td>
                    <td>{item.answer}</td>
                    <td>{item.is_show ? "Ya" : "Tidak"}</td>

                    <td className="text-center">
                      <Input
                        className="checkbox_animated"
                        id={`detach-${item.id}`}
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => handleCheckboxChange(item)}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
          <CardBody>
            {isLoading === false && pagination && (
              <PaginationComponent
                pagination={pagination}
                handleBackPagination={() =>
                  handleBackPagination(
                    pagination,
                    filter,
                    dispatch,
                    setFilters,
                    setRefatch
                  )
                }
                handleClickPagination={(page) =>
                  handleClickPagination(
                    page,
                    filter,
                    dispatch,
                    setFilters,
                    setRefatch
                  )
                }
                handleNextPagination={() =>
                  handleNextPagination(
                    pagination,
                    filter,
                    dispatch,
                    setFilters,
                    setRefatch
                  )
                }
              />
            )}
          </CardBody>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit}>
            Simpan
          </Button>{" "}
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ModalAddFAQ